/**
 * SPEC-TrainingSimulator §8.2 (D1) — the delivery clone.
 *
 * Renders the agent detail layout (hero, badges, Delivery Details, Completion
 * Details, Photos panel) from a snapshot instead of a live delivery. It
 * mounts the SAME components the live page uses (`components/qa/*`) and the
 * same tokens as qa-detail-v4.tsx, and never touches claims, sockets, or
 * the live delivery API. Add flag is rendered but inert.
 *
 * The clone body reads ONLY the trainee view. `ModelSaysBox` is the one
 * place `expected` / `actual` appear, for the key editor (§7.2).
 */

import { useEffect, useRef, useState } from "react";
import { ChecklistCard } from "@/components/qa/checklist-card";
import { CompletionDetailsCard } from "@/components/qa/completion-details-card";
import { PhotosPanel, type PhotosPanelPhoto, type PhotosPanelVerifiedPast } from "@/components/qa/photos-panel";
import { PhotoBoxPanel, type PhotoBox, type PhotoBoxLayer, type PhotoPick } from "@/components/simulator/photo-box-panel";
import { COMPLETION_FIELDS, DELIVERY_DETAILS_FIELDS, TextHighlightSurface, type HighlightLayer } from "@/components/simulator/text-highlight";
import { Starling } from "@/lib/starling-brand";
import type { TraineeSnapshot } from "@shared/simulator-helpers";

export const CLONE_TOKENS = {
  bg: "#F7F8FA",
  card: "#FFFFFF",
  border: "#E5E7EB",
  text: "#111827",
  txt2: "#6B7280",
  txtMuted: "#9CA3AF",
  purple: Starling.purple,
  purpleSub: "rgba(89,47,150,.12)",
  purpleSubB: "rgba(89,47,150,.18)",
  flag: "#B45309",
  flagBorder: "#FDE68A",
  sub: "#F3F4F6",
} as const;

function formatWindow(start: string | null | undefined, end: string | null | undefined, completedAt: string | null | undefined): string {
  const fmt = (iso: string | null | undefined) => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };
  const s = fmt(start);
  const e = fmt(end);
  const c = fmt(completedAt);
  if (s && e && c) return `Window ${s}–${e} · delivered ${c}`;
  if (s && e) return `Window ${s}–${e}`;
  if (c) return `Delivered ${c}`;
  return "—";
}

export interface SimulatorDeliveryViewProps {
  snapshot: TraineeSnapshot;
  /** Wraps the Completion Details card so q7's quote capture can scope its selection. */
  completionNoteElementId?: string;
  /** Wraps the Delivery Details card so q1/q2's street/unit highlight capture can scope its selection. */
  deliveryDetailsElementId?: string;
  /** Optional status badge text (e.g. "Key editor · preview"). */
  statusLabel?: string;
  /** Grid layout: `page` mirrors v4 (1fr 1.3fr); `stack` puts photos under the cards for a narrow pane. */
  layout?: "page" | "stack";
  /**
   * Key editor only — pointers for the trainee: the Photos card becomes the
   * drawable PhotoBoxPanel, and the Delivery Details / Completion Details
   * cards outline the anchored text highlights. Absent in the runner until
   * the question is submitted (§8.3), which then mounts the same surfaces.
   */
  pointers?: {
    layers: Array<PhotoBoxLayer & HighlightLayer>;
    /** Question id whose layer is editable; null → view only. */
    activeKind: string | null;
    activeLabel?: string;
    onChangeBoxes: (boxes: PhotoBox[]) => void;
    /** Runner: pointers are shown, never drawn or removed. */
    readOnly?: boolean;
  };
  /** Runner only (§8.3): "where" questions put the Photos card in pick mode. Needs `pointers`. */
  photoPick?: PhotoPick | null;
  /** Runner only (§8.3): q6 = Yes switches the Photos card to Verified past. */
  photoTab?: "pod" | "past" | null;
  /**
   * Runner only: a trainee has no Onfleet / Podium / Slack access yet, so every
   * outbound link the cloned cards render ("View in Onfleet" today) is greyed
   * and cannot navigate — it stays visible so the real page still looks
   * familiar. The key editor leaves this off and keeps working links.
   */
  inertLinks?: boolean;
}

/** Kills navigation for every link inside the clone when `inertLinks` is set. */
const INERT_LINKS_CSS = `
[data-inert-links="true"] a[href]{pointer-events:none !important;cursor:not-allowed !important;opacity:.5;filter:grayscale(1)}
`;

/**
 * The cloned cards own their own markup, so the disabled semantics are applied
 * to their links from here: out of the tab order, announced as disabled, and
 * carrying the same tooltip as Add flag / CX Notified / Skip
 * (AUDIT-TraineeUI L5). Re-applied after every render because a card re-render
 * replaces the anchor.
 */
function useInertLinks(ref: React.RefObject<HTMLDivElement>, enabled: boolean | undefined) {
  useEffect(() => {
    if (!enabled) return;
    for (const a of Array.from(ref.current?.querySelectorAll<HTMLAnchorElement>("a[href]") ?? [])) {
      a.tabIndex = -1;
      a.setAttribute("aria-disabled", "true");
      a.setAttribute("title", "Not available in training");
    }
  });
}

export function SimulatorDeliveryView({ snapshot, completionNoteElementId, deliveryDetailsElementId, statusLabel, layout = "page", pointers, photoPick, photoTab, inertLinks }: SimulatorDeliveryViewProps) {
  const T = CLONE_TOKENS;
  const rootRef = useRef<HTMLDivElement>(null);
  useInertLinks(rootRef, inertLinks);
  const textSurface = (id: string | undefined, fields: typeof DELIVERY_DETAILS_FIELDS, child: React.ReactNode) =>
    pointers ? (
      <TextHighlightSurface id={id} layers={pointers.layers} activeKind={pointers.activeKind} fields={fields}>{child}</TextHighlightSurface>
    ) : (
      <div id={id}>{child}</div>
    );
  const podPhotos: PhotosPanelPhoto[] = (snapshot.podPhotos ?? []).map((p) => ({ id: p.photoId, url: p.url, order: p.index - 1 }));
  const verifiedPast: PhotosPanelVerifiedPast[] = (snapshot.verifiedPast ?? []).map((v, i) => ({
    id: `past-${i}`, completedAt: v.completedAt, dropZone: v.dropZone, photoUrl: v.photoUrl, verifiedBy: v.verifiedBy,
  }));
  return (
    <div
      ref={rootRef}
      data-testid="simulator-delivery-clone"
      data-inert-links={inertLinks ? "true" : undefined}
      onClickCapture={inertLinks ? (e) => { if ((e.target as HTMLElement | null)?.closest?.("a[href]")) { e.preventDefault(); e.stopPropagation(); } } : undefined}
      style={{ background: T.bg, fontFamily: Starling.fontBody, color: T.text, padding: "14px 16px", borderRadius: 10, border: `1px solid ${T.border}` }}
    >
      {inertLinks && <style data-testid="clone-inert-links">{INERT_LINKS_CSS}</style>}
      {/* Title row — breadcrumb, same as the live page. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 18, fontWeight: 600, marginBottom: 12 }} data-testid="clone-breadcrumb">
        <span>{snapshot.customer ?? "—"}</span>
        <span style={{ color: T.txtMuted, fontWeight: 500 }}>›</span>
        <span>{snapshot.recipientName ?? "—"}</span>
      </div>

      {/* Hero */}
      <div data-testid="clone-hero" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "14px 16px", marginBottom: 12, boxShadow: "0 1px 2px rgba(16,24,40,.06)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
          <div style={{ fontFamily: Starling.fontHead, fontSize: 18, fontWeight: 600 }} data-testid="clone-recipient">
            {snapshot.recipientName ?? "Unknown recipient"}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
            <div style={{ padding: "6px 11px", borderRadius: 8, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", background: T.purpleSub, color: T.purple }}>
              {formatWindow(snapshot.windowStart, snapshot.windowEnd, snapshot.completedAt)}
            </div>
            <div style={{ padding: "6px 11px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: T.sub, color: T.text, whiteSpace: "nowrap" }}>
              🚚 {snapshot.driverName ?? "Unknown driver"}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 10, flexWrap: "wrap" }}>
          <span data-testid="clone-verified-badge" style={{ display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 999, padding: "3px 9px", fontSize: 11.5, fontWeight: 600, background: T.purpleSub, color: T.purple, border: `1px solid ${T.purpleSubB}` }}>
            ✓ {snapshot.verifiedPastCount ?? 0}× verified
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", borderRadius: 999, padding: "3px 9px", fontSize: 11.5, fontWeight: 600, background: T.sub, color: T.txt2, border: `1px solid ${T.border}` }}>
            {statusLabel ?? "In Progress"}
          </span>
          <button
            type="button"
            title="Not available in training"
            aria-disabled="true"
            data-testid="clone-add-flag"
            style={{ display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 999, padding: "3px 9px", fontSize: 11.5, fontWeight: 600, background: "#fff", color: T.flag, border: `1px solid ${T.flagBorder}`, cursor: "not-allowed", opacity: 0.7, fontFamily: Starling.fontBody }}
          >
            ⚑ Add flag
          </button>
        </div>
      </div>

      {/* Body grid — identical composition to qa-detail-v4's agent layout. */}
      <div style={{ display: "grid", gridTemplateColumns: layout === "page" ? "1fr 1.3fr" : "1fr", gap: 12 }} data-testid="clone-grid">
        <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          {textSurface(deliveryDetailsElementId, DELIVERY_DETAILS_FIELDS, (
            <ChecklistCard
              company={snapshot.customer ?? null}
              recipientName={snapshot.recipientName ?? null}
              address={snapshot.address || "—"}
              destinationNotes={snapshot.destinationNotes ?? null}
              taskNotes={snapshot.taskNotes ?? null}
              taskType={snapshot.taskType ?? null}
            />
          ))}
          {textSurface(completionNoteElementId, COMPLETION_FIELDS, (
            <CompletionDetailsCard
              completedAt={snapshot.completedAt}
              scanId={snapshot.scanId}
              completionNote={snapshot.driverCompletionNotes ?? null}
              onfleetTaskId={snapshot.onfleetTaskId ?? null}
              slackThreadUrl={null}
            />
          ))}
        </div>
        <div style={{ minWidth: 0 }}>
          {pointers ? (
            <PhotoBoxPanel
              podPhotos={snapshot.podPhotos ?? []}
              verifiedPast={snapshot.verifiedPast ?? []}
              verifiedPastCount={snapshot.verifiedPastCount ?? 0}
              layers={pointers.layers}
              activeKind={pointers.activeKind}
              activeLabel={pointers.activeLabel}
              onChangeActive={pointers.onChangeBoxes}
              pick={photoPick ?? null}
              requestedTab={photoTab ?? null}
              quietBanner={!!pointers.readOnly}
              readOnly={pointers.readOnly}
            />
          ) : (
            <PhotosPanel viewMode="agent" podPhotos={podPhotos} verifiedPast={verifiedPast} verifiedPastCount={snapshot.verifiedPastCount} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Key-editor only ─────────────────────────────────────────────────────────

export interface ModelSaysBoxProps {
  expected: { street: string | null; unit: string | null; dz: string | null };
  actual: { street: string | null; unit: string | null; dz: string | null };
}

/** §7.2 — collapsible "Model says" box. The only surface that shows expected/actual. */
export function ModelSaysBox({ expected, actual }: ModelSaysBoxProps) {
  const [open, setOpen] = useState(false);
  const T = CLONE_TOKENS;
  const cell = (v: string | null) => (v && v.trim() ? v : <span style={{ color: T.txtMuted }}>—</span>);
  return (
    <div data-testid="model-says-box" style={{ border: `1px dashed ${T.purpleSubB}`, borderRadius: 8, background: T.card, marginTop: 12 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="model-says-toggle"
        style={{ width: "100%", textAlign: "left", padding: "8px 12px", background: "transparent", border: "none", cursor: "pointer", fontFamily: Starling.fontBody, fontSize: 12.5, fontWeight: 600, color: T.purple }}
      >
        {open ? "▾" : "▸"} Model says <span style={{ color: T.txtMuted, fontWeight: 500 }}>(never shown to trainees — key from the photos, not from this)</span>
      </button>
      {open && (
        <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse", margin: "0 0 8px" }}>
          <thead>
            <tr style={{ color: T.txt2, textAlign: "left" }}>
              <th style={{ padding: "4px 12px", fontWeight: 600 }}></th>
              <th style={{ padding: "4px 12px", fontWeight: 600 }}>Street</th>
              <th style={{ padding: "4px 12px", fontWeight: 600 }}>Unit</th>
              <th style={{ padding: "4px 12px", fontWeight: 600 }}>Drop zone</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={{ padding: "4px 12px", fontWeight: 600 }}>Expected</td><td style={{ padding: "4px 12px" }}>{cell(expected.street)}</td><td style={{ padding: "4px 12px" }}>{cell(expected.unit)}</td><td style={{ padding: "4px 12px" }}>{cell(expected.dz)}</td></tr>
            <tr><td style={{ padding: "4px 12px", fontWeight: 600 }}>Actual</td><td style={{ padding: "4px 12px" }}>{cell(actual.street)}</td><td style={{ padding: "4px 12px" }}>{cell(actual.unit)}</td><td style={{ padding: "4px 12px" }}>{cell(actual.dz)}</td></tr>
          </tbody>
        </table>
      )}
    </div>
  );
}
